import { useState } from "react";
import {
  Box,
  IconButton,
  Typography,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  Avatar,
  TextField,
  Checkbox,
  FormControlLabel,
  Divider,
  Stack,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  useTheme,
  InputLabel,
  CircularProgress,
} from "@mui/material";
import {
  QrCode,
  Email,
  WhatsApp,
  FacebookOutlined,
  Close,
} from "@mui/icons-material";
import QRCode from "react-qr-code";
import { tokens } from "../../../../../theme";
import { IThemeConfig } from "./types/memorial-types";
import { Copy } from "lucide-react";
import {
  showSuccessToast,
  showErrorToast,
} from "../../../../../components/common/ShowToast";
import SimCardDownloadOutlinedIcon from "@mui/icons-material/SimCardDownloadOutlined";
import apiService from "../../../../../service/Api";
import AddToCalendarButton from "./AddToCalendar";
import type { CalendarEvent } from "./types/AddtoCalendarutils";

interface ShareControlsProps {
  theme: IThemeConfig;
  streamData: {
    id: number;
    website_stream_link?: string;
    title?: string;
    booking?: {
      id: number;
      service: string;
      deceased?: Array<{
        id: number;
        person_image?: string;
      }>;
    };
  } | null;
  donationsEnabled?: boolean;
  streamProgram?: string | null;
  downloadingProgram?: boolean;
  handleDownloadProgram?: () => void;
  onDonationSuccess?: () => void;
  calendarEvent?: CalendarEvent | null;
}

export default function ShareControls({
  streamData,
  theme,
  donationsEnabled = false,
  streamProgram,
  handleDownloadProgram,
  downloadingProgram,
  onDonationSuccess,
  calendarEvent,
}: ShareControlsProps) {
  const muiTheme = useTheme();
  const colors = tokens(muiTheme.palette.mode);
  const [openQr, setOpenQr] = useState(false);
  const [openDonationDialog, setOpenDonationDialog] = useState(false);
  const isLocal =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1";
  const [selectedAmount, setSelectedAmount] = useState<number | null>(
    isLocal ? 0.01 : 10,
  );
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [donorName, setDonorName] = useState<string>("");
  const [memory, setMemory] = useState("");
  const [currency, setCurrency] = useState<string>("USD");
  const [phone, setPhone] = useState<string>("");
  const [phoneError, setPhoneError] = useState<string>("");
  const [paymentMethod, setPaymentMethod] = useState<string>("ecocash");
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<string | null>(null);
  const [, setPollingDonationId] = useState<number | null>(null);
  const [pollMessage, setPollMessage] = useState<string>("");

  const validatePhone = (value: string): string => {
    const digits = value.replace(/[^0-9]/g, "");
    if (!digits) return "Please enter your phone number";
    if (digits.startsWith("0") && digits.length !== 10)
      return "Phone number must be 10 digits (e.g. 0771234567)";
    if (digits.startsWith("263") && digits.length !== 12)
      return "Phone number must be 12 digits with country code (e.g. 263771234567)";
    if (!digits.startsWith("0") && !digits.startsWith("263"))
      return "Phone number must start with 0 or 263";
    const validPrefixes = ["071", "073", "077", "078", "086"];
    const local = digits.startsWith("263") ? "0" + digits.slice(3) : digits;
    const prefix = local.slice(0, 3);
    if (!validPrefixes.some((p) => prefix === p))
      return "Please enter a valid Zimbabwean mobile number";
    return "";
  };

  const handlePhoneChange = (value: string) => {
    const cleaned = value.replace(/[^0-9+]/g, "");
    setPhone(cleaned);
    if (phoneError) setPhoneError(validatePhone(cleaned));
  };

  const handleWhatsAppShare = () => {
    if (!streamData?.website_stream_link) return;
    const messageTemplate = `It is with great sadness that we share the livestream link for the funeral ${streamData.title}.\n\nYou can view it here: ${streamData.website_stream_link}`;
    const text = encodeURIComponent(messageTemplate);
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };

  const handleEmailShare = () => {
    if (!streamData?.website_stream_link) return;
    const subject = encodeURIComponent(
      `Funeral Service for ${streamData.title} - Livestream Link`,
    );
    const body = encodeURIComponent(
      `Dear friends and family,\n\nWe are sharing the livestream link for the funeral service of ${streamData.title}\n\nYou can view the livestream here: ${streamData.website_stream_link}\n\nThank you for your thoughts and support during this difficult time.\n\nSincerely,`,
    );
    window.open(`mailto:?subject=${subject}&body=${body}`, "_blank");
  };

  const handleCopyUrl = async () => {
    if (!streamData?.website_stream_link) return;
    try {
      await navigator.clipboard.writeText(streamData.website_stream_link);
      showSuccessToast("Link Copied Successfully");
    } catch (err) {
      console.error("Failed to copy URL: ", err);
    }
  };

  const handleOpenDonationDialog = () => {
    if (!donationsEnabled) return;
    setOpenDonationDialog(true);
  };

  const handleCloseDonationDialog = () => {
    setOpenDonationDialog(false);
    if (!isProcessing) {
      setPaymentStatus(null);
      setPollingDonationId(null);
      setPollMessage("");
      setPhone("");
      setPhoneError("");
      setDonorName("");
      setIsAnonymous(false);
      setSelectedAmount(isLocal ? 0.01 : 10);
    }
  };

  const pollPaymentStatus = async (donationId: number) => {
    const maxAttempts = 60;
    let attempts = 0;

    const poll = async () => {
      if (attempts >= maxAttempts) {
        setPaymentStatus("timeout");
        setIsProcessing(false);
        setPollMessage(
          "Payment timed out. Please check your phone and try again.",
        );
        showErrorToast(
          "Payment timed out. Please check your phone and try again.",
        );
        return;
      }

      attempts++;
      try {
        const res = await apiService.get<{
          paid: boolean;
          status: string;
          message: string;
          donation_id: number;
          amount?: string;
          currency?: string;
        }>(`/api/chema/donations/${donationId}/status/`);

        const { paid, status: paymentStat, message } = res.data || {};

        if (paid) {
          setPaymentStatus("paid");
          setIsProcessing(false);
          setPollingDonationId(null);
          setPollMessage(
            message || "Payment confirmed. Thank you for your donation!",
          );
          showSuccessToast(
            message || "Payment received! Thank you for your donation.",
          );
          onDonationSuccess?.();
          setTimeout(() => handleCloseDonationDialog(), 3000);
          return;
        }

        const currentStatus = paymentStat || "pending";
        setPaymentStatus(currentStatus);
        setPollMessage(message || "Checking payment status...");

        if (currentStatus === "cancelled" || currentStatus === "failed") {
          setIsProcessing(false);
          setPollingDonationId(null);
          showErrorToast(
            message || `Payment ${currentStatus}. Please try again.`,
          );
          return;
        }

        setTimeout(poll, 5000);
      } catch {
        setTimeout(poll, 5000);
      }
    };

    poll();
  };

  const handleProceedWithPayment = async () => {
    if (!selectedAmount || selectedAmount <= 0) {
      showErrorToast("Please enter a donation amount greater than zero");
      return;
    }

    const phoneValidation = validatePhone(phone);
    if (phoneValidation) {
      setPhoneError(phoneValidation);
      showErrorToast(phoneValidation);
      return;
    }

    if (!streamData?.id) {
      showErrorToast(
        "Stream information is missing. Please refresh the page and try again.",
      );
      return;
    }

    setIsProcessing(true);
    setPaymentStatus("sending");

    try {
      const response = await apiService.post<{
        success: boolean;
        donation_id: number;
        message: string;
        instructions: string;
      }>("/api/chema/donations/initiate/mobile/", {
        amount: selectedAmount,
        stream_id: streamData.id,
        currency: currency,
        phone: phone,
        method: paymentMethod,
        donor_name: isAnonymous ? "" : donorName,
        is_anonymous: isAnonymous,
      });

      if (response.data?.success) {
        setPaymentStatus("awaiting_approval");
        setPollingDonationId(response.data.donation_id);
        showSuccessToast(
          response.data.message || "Check your phone to approve the payment.",
        );
        pollPaymentStatus(response.data.donation_id);
      } else {
        showErrorToast("Payment initialization failed. Please try again.");
        setIsProcessing(false);
        setPaymentStatus(null);
      }
    } catch (error: unknown) {
      console.error("Donation initiation error:", error);
      const errorMessage =
        error instanceof Object &&
        "response" in error &&
        typeof error.response === "object" &&
        error.response !== null &&
        "data" in error.response &&
        typeof error.response.data === "object" &&
        error.response.data !== null &&
        "error" in error.response.data
          ? ((error.response.data as Record<string, unknown>).error as string)
          : "Failed to initiate payment. Please try again.";
      showErrorToast(errorMessage);
      setIsProcessing(false);
      setPaymentStatus(null);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        mb: 2,
      }}
    >
      {/* ── Left: Download Program + Add to Calendar + Donate ── */}
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}
      >
        {/* Download Program */}
        <Button
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            borderRadius: "60px",
            py: 0.6,
            px: 1.5,
            boxShadow: 0,
            textTransform: "none",
            opacity: streamProgram ? 1 : 0.5,
          }}
          variant="contained"
          size="small"
          onClick={handleDownloadProgram}
          disabled={!streamProgram || downloadingProgram}
        >
          {downloadingProgram ? (
            <CircularProgress size={18} sx={{ color: "#fff" }} />
          ) : (
            <>
              <SimCardDownloadOutlinedIcon
                sx={{ color: "#fff", fontSize: 18 }}
              />
              <Typography
                sx={{ color: "#fff", fontWeight: "bold", fontSize: "0.8rem" }}
              >
                Download Program
              </Typography>
            </>
          )}
        </Button>

        {/* Add to Calendar — only renders when calendarEvent prop is passed */}
        {calendarEvent && (
          <AddToCalendarButton
            event={calendarEvent}
            variant="outlined"
            size="small"
            label="Add to Calendar"
          />
        )}

        {/* Donate */}
        {streamData?.booking?.service !== "Conference" && donationsEnabled && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              cursor: "pointer",
            }}
            onClick={handleOpenDonationDialog}
          >
            <img
              src="https://www.paynow.co.zw/Content/Buttons/Medium_buttons/button_donate_medium.png"
              alt="Donate via Paynow"
              style={{ border: 0 }}
            />
          </Box>
        )}
      </Box>

      {/* ── Right: Social share icons ── */}
      <Box sx={{ display: "flex", gap: 1.5 }}>
        <IconButton
          sx={{
            backgroundColor: "#25D366",
            color: theme.surface,
            "&:hover": { backgroundColor: "#1DA851" },
            mr: 1,
          }}
          onClick={handleWhatsAppShare}
        >
          <WhatsApp />
        </IconButton>

        <IconButton
          sx={{
            backgroundColor: "info.main",
            color: theme.surface,
            "&:hover": { backgroundColor: "info.light" },
            mr: 1,
          }}
        >
          <FacebookOutlined />
        </IconButton>

        <IconButton
          sx={{
            backgroundColor: "#EA4335",
            color: theme.surface,
            "&:hover": { backgroundColor: "#D63A2C" },
            mr: 1,
          }}
          onClick={handleEmailShare}
        >
          <Email />
        </IconButton>

        <IconButton
          sx={{
            backgroundColor: colors.primary[900],
            color: colors.primary[700],
            mr: 1,
          }}
          onClick={() => setOpenQr(true)}
        >
          <QrCode />
        </IconButton>

        <IconButton
          onClick={handleCopyUrl}
          sx={{
            backgroundColor: "#000",
            color: "#fff",
            "&:hover": { backgroundColor: "#807d7dff" },
          }}
        >
          <Copy />
        </IconButton>
      </Box>

      {/* QR Dialog */}
      <Dialog open={openQr} onClose={() => setOpenQr(false)}>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          <QRCode value={streamData?.website_stream_link || ""} size={200} />
        </DialogContent>
      </Dialog>

      {/* Donation / Chema Dialog */}
      <Dialog
        open={openDonationDialog}
        onClose={handleCloseDonationDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar
                src={streamData?.booking?.deceased?.[0]?.person_image || ""}
                sx={{ width: 56, height: 56 }}
              >
                {streamData?.title ? streamData.title.charAt(0) : "C"}
              </Avatar>
              <Box>
                <Typography variant="subtitle2">
                  Your contribution helps{" "}
                  <Typography component="span" fontWeight="bold">
                    {streamData?.title?.replace("Stream for ", "") || "The"}{" "}
                    Family
                  </Typography>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Helping today builds stronger ties for tomorrow.
                </Typography>
              </Box>
            </Stack>
            <IconButton
              onClick={handleCloseDonationDialog}
              size="small"
              sx={{ ml: "auto" }}
            >
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent>
          <Typography variant="body2" sx={{ mt: 2, mb: 1, fontWeight: 500 }}>
            Enter Your Donation
          </Typography>

          <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Donation Amount"
              value={selectedAmount ?? ""}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9.]/g, "");
                setSelectedAmount(value.length > 0 ? parseFloat(value) : null);
              }}
              fullWidth
              disabled={isProcessing}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography>$</Typography>
                  </InputAdornment>
                ),
              }}
            />
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel id="currency-label">Select Currency</InputLabel>
              <Select
                labelId="currency-label"
                id="currency-select"
                value={currency}
                label="Select Currency"
                onChange={(e) => setCurrency(e.target.value)}
                disabled={isProcessing}
              >
                <MenuItem value="USD">USD</MenuItem>
                <MenuItem value="ZWL">ZWL</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          <Stack
            direction="row"
            spacing={1}
            justifyContent="space-between"
            mt={3}
            flexWrap="wrap"
          >
            {(isLocal ? [0.01, 0.1, 0.5, 1, 5, 10] : [10, 20, 30, 50, 60]).map(
              (amount) => (
                <Button
                  key={amount}
                  variant={selectedAmount === amount ? "contained" : "outlined"}
                  onClick={() => setSelectedAmount(amount)}
                  disabled={isProcessing}
                  sx={{
                    flex: "1 1 80px",
                    m: 0.5,
                    borderRadius: 2,
                    textTransform: "none",
                  }}
                >
                  ${amount}
                </Button>
              ),
            )}
          </Stack>

          <Divider sx={{ my: 3 }} />

          <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
            Mobile Money Payment
          </Typography>

          <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Phone Number"
              placeholder="e.g. 0771234567"
              value={phone}
              onChange={(e) => handlePhoneChange(e.target.value)}
              onBlur={() => setPhoneError(validatePhone(phone))}
              error={!!phoneError}
              helperText={
                phoneError || "Enter your EcoCash, OneMoney or InnBucks number"
              }
              fullWidth
              disabled={isProcessing}
            />
            <FormControl sx={{ minWidth: 160 }}>
              <InputLabel id="method-label">Payment Method</InputLabel>
              <Select
                labelId="method-label"
                id="method-select"
                value={paymentMethod}
                label="Payment Method"
                onChange={(e) => setPaymentMethod(e.target.value)}
                disabled={isProcessing}
              >
                <MenuItem value="ecocash">EcoCash</MenuItem>
                <MenuItem value="onemoney" disabled>
                  OneMoney
                </MenuItem>
                <MenuItem value="innbucks" disabled>
                  InnBucks
                </MenuItem>
              </Select>
            </FormControl>
          </Stack>

          {paymentStatus &&
            paymentStatus !== "paid" &&
            paymentStatus !== "cancelled" &&
            paymentStatus !== "failed" &&
            paymentStatus !== "timeout" && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: "info.light",
                  color: "info.contrastText",
                  textAlign: "center",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                }}
              >
                <CircularProgress size={20} />
                <Typography variant="body2">
                  {pollMessage ||
                    "Waiting for payment approval on your phone..."}
                </Typography>
              </Box>
            )}

          {paymentStatus === "paid" && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                borderRadius: 2,
                bgcolor: "success.light",
                color: "success.contrastText",
                textAlign: "center",
              }}
            >
              <Typography variant="body2" fontWeight="bold">
                {pollMessage ||
                  "Payment received! Thank you for your donation."}
              </Typography>
            </Box>
          )}

          {(paymentStatus === "cancelled" ||
            paymentStatus === "failed" ||
            paymentStatus === "timeout") && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                borderRadius: 2,
                bgcolor: "error.light",
                color: "error.contrastText",
                textAlign: "center",
              }}
            >
              <Typography variant="body2" fontWeight="bold">
                {pollMessage || "Payment was not completed. Please try again."}
              </Typography>
            </Box>
          )}

          <Divider sx={{ my: 3 }} />

          <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
            Your Details
          </Typography>

          <FormControlLabel
            control={
              <Checkbox
                checked={isAnonymous}
                onChange={(e) => {
                  setIsAnonymous(e.target.checked);
                  if (e.target.checked) setDonorName("");
                }}
                disabled={isProcessing}
              />
            }
            label={<Typography variant="body2">Donate anonymously</Typography>}
          />

          {!isAnonymous && (
            <TextField
              label="Your Name"
              placeholder="Enter your name"
              value={donorName}
              onChange={(e) => setDonorName(e.target.value)}
              fullWidth
              disabled={isProcessing}
              sx={{ mt: 1 }}
            />
          )}

          <TextField
            label={`Leave a memory for ${streamData?.title || "the deceased"}`}
            multiline
            minRows={3}
            fullWidth
            value={memory}
            onChange={(e) => setMemory(e.target.value)}
            sx={{ mt: 2 }}
            disabled={isProcessing}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleProceedWithPayment}
            variant="contained"
            color="primary"
            fullWidth
            disabled={isProcessing}
            sx={{ borderRadius: "30px", textTransform: "none", py: 1.2 }}
          >
            {isProcessing ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1, color: "#fff" }} />
                {paymentStatus === "awaiting_approval"
                  ? "Waiting for approval..."
                  : "Processing..."}
              </>
            ) : paymentStatus === "paid" ? (
              "Payment Complete"
            ) : (
              "Pay via Mobile Money"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
